import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Alert,
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SharedButton from '../../shared/components/Button'
import SharedDialog from '../../shared/components/Dialog'
import InputWithValidation from '../../shared/components/InputWithValidation'
import StatusChip from '../../shared/components/StatusChip'
import EmptyState from '../../shared/components/EmptyState'
import { useIsSuperAdmin, useThemeMode } from '../../shared/hooks'
import { getDialogTextFieldSx } from '../../shared/styles/formStyles'
import { signingChipConfig } from '../../shared/utilities/statusConfig'
import { notify } from '../../shared/utilities/util'
import type { BaseQueryErrorShape } from '../../store/api/baseQuery'
import { useGetSigningProviderQuery, useSetSigningProviderMutation } from '../../store/api/adminApi'
import type { SigningProviderKindRow, SigningProviderValue, SigningReadinessCheck } from '../../store/api/adminApi'
import { A2pRecordDialog, A2pWithdrawDialog, DialogError } from './eSignatureA2p'
import {
  REASON_MAX_LENGTH,
  NOT_BUILT_HINT,
  a2pAction,
  bannerView,
  canConfirm,
  checkDetail,
  checkStatusLabel,
  dialogCopy,
  inhouseDisabledReason,
  kindLabel,
  kindSubLabel,
  lastChangedView,
  nextTarget,
  providerLabel,
  readinessChip,
} from './eSignatureModel'
import type { A2pAction } from './eSignatureModel'

/**
 * Subscriber detail, E-signature tab (approved mockup v2, 2026-09-24; v3, 2026-10-01: the signing
 * link key check and the A2P 10DLC confirmation; 2026-10-14: the two compliance checks removed).
 * SuperAdmin only: the tab is not rendered for anyone else,
 * and every route behind it is SuperAdmin-checked server-side. The A2P Record / Withdraw actions are
 * gated on the same rule again here, so they never show for an operator who cannot switch.
 *
 * Re-checked on every open: the query refetches on mount, so switching back to this tab re-runs
 * the readiness checks on the server. Every change, either direction, goes through a dialog with
 * a required reason that the server writes to the audit log with the operator's name.
 */

interface PendingChange {
  kind: string
  target: SigningProviderValue
}

const CheckIcon = ({ status }: { status: SigningReadinessCheck['status'] }) => {
  if (status === 'pass') return <CheckCircleOutlineIcon sx={{ fontSize: 22, color: signingChipConfig.pass.color }} />
  if (status === 'fail') return <CancelOutlinedIcon sx={{ fontSize: 22, color: signingChipConfig.fail.color }} />
  return <HelpOutlineIcon sx={{ fontSize: 22, color: signingChipConfig.notChecked.color }} />
}

const Chip = ({ tone }: { tone: keyof typeof signingChipConfig }) => {
  const config = signingChipConfig[tone]
  return <StatusChip {...config} />
}

const ESignature = ({ id, companyName }: { id?: string; companyName: string }) => {
  const { isDark } = useThemeMode()
  const { data, isLoading, isFetching, isError } = useGetSigningProviderQuery(id ?? '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  })
  const [setSigningProvider, { isLoading: saving }] = useSetSigningProviderMutation()
  const canManage = useIsSuperAdmin()
  const [a2pDialog, setA2pDialog] = useState<A2pAction | null>(null)

  const [pending, setPending] = useState<PendingChange | null>(null)
  const [attested, setAttested] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<{ reason: string }>({ defaultValues: { reason: '' } })
  const reason = watch('reason') ?? ''

  const paperSx = {
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
    borderRadius: '12px',
    overflow: 'hidden',
  }
  const headerSx = {
    px: { xs: 2, sm: 2.5 },
    py: 1.75,
    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap' as const,
  }

  const openDialog = (row: SigningProviderKindRow, target: SigningProviderValue | null) => {
    if (!target) return
    reset({ reason: '' })
    setAttested(false)
    setSubmitError(null)
    setPending({ kind: row.kind, target })
  }

  const closeDialog = () => {
    if (saving) return
    setPending(null)
  }

  const onConfirm = handleSubmit(async ({ reason: typed }) => {
    if (!pending || !id) return
    setSubmitError(null)
    try {
      await setSigningProvider({
        companyId: id,
        kind: pending.kind,
        provider: pending.target,
        reason: typed.trim(),
      }).unwrap()
      notify(`${kindLabel(pending.kind)} now signs with ${providerLabel(pending.target)}`, 'success')
      setPending(null)
    } catch (error) {
      const failure = error as BaseQueryErrorShape
      setSubmitError(failure?.message || 'The signing provider could not be changed. Please try again.')
    }
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rectangular" height={260} sx={{ borderRadius: '12px' }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '12px' }} />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <EmptyState
        icon={<ErrorOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />}
        title="E-signature settings could not be loaded"
        subtitle="Reload the page to try again."
      />
    )
  }

  const banner = bannerView(data)
  const chip = readinessChip(data.readiness)
  const copy = pending ? dialogCopy(pending.kind, pending.target, companyName) : null
  const alertSeverity = banner.tone === 'ok' ? 'success' : banner.tone === 'warn' ? 'warning' : 'info'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert severity={alertSeverity} sx={{ borderRadius: '10px', fontSize: '13px' }}>
        <strong>{banner.title}</strong>
        {banner.lines.map((line) => (
          <Box component="span" key={line} sx={{ display: 'block' }}>
            {line}
          </Box>
        ))}
      </Alert>

      <Paper elevation={0} sx={paperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}>
              Signing provider by document kind
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mt: 0.25 }}>
              Only new sends change. Documents already out for signature finish where they started.
            </Typography>
          </Box>
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                {['Document kind', 'Provider', 'Current value', 'Last changed'].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', py: 1.25 }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.kinds.map((row) => {
                const inhouseReason = inhouseDisabledReason(row, data.readiness)
                const changed = lastChangedView(row)
                const label = kindLabel(row.kind)
                return (
                  <TableRow key={row.kind}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: '13.5px', color: 'text.primary' }}>{label}</Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        {kindSubLabel(row.kind)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        // A refetch in flight may still show the previous readiness: no switch until it lands.
                        disabled={isFetching}
                        value={row.effective}
                        onChange={(_event, value: SigningProviderValue | null) =>
                          openDialog(row, nextTarget(row, value))
                        }
                        aria-label={`${label} provider`}
                      >
                        <ToggleButton
                          value="boldsign"
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12.5px', px: 1.75 }}
                        >
                          BoldSign
                        </ToggleButton>
                        <ToggleButton
                          value="inhouse"
                          disabled={!!inhouseReason}
                          title={inhouseReason ?? undefined}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12.5px', px: 1.75 }}
                        >
                          In-house
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {/* A disabled MUI button gets no pointer events, so its title never shows: say why in text. */}
                      {inhouseReason && row.effective !== 'inhouse' && (
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                          {inhouseReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip tone={row.effective} />
                        {row.stored === null && (
                          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>default</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: '13.5px', color: 'text.primary' }}>{changed.primary}</Typography>
                      {changed.secondary && (
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{changed.secondary}</Typography>
                      )}
                      {row.lastChange && !row.implemented && (
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{NOT_BUILT_HINT}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper elevation={0} sx={paperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}>
              Readiness for in-house signing
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mt: 0.25 }}>
              Re-checked every time this page opens. Each item is fixed on the company&apos;s own settings or by
              platform staff; only the text messaging registration is recorded here. If a check later fails, the toggle
              stays on and a banner appears above.
            </Typography>
          </Box>
          <StatusChip {...signingChipConfig[chip.tone === 'ok' ? 'pass' : 'fail']} label={chip.label} />
        </Box>
        {data.readiness.checks.map((check, index) => {
          // Guarded: a backend from before the A2P confirmation routes sends no `a2p10dlcConfirmation`,
          // and then there is nothing to record against.
          const action = data.a2p10dlcConfirmation ? a2pAction(check, data.a2p10dlcConfirmation, canManage) : null
          return (
            <Box
              key={check.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                gap: 1.5,
                alignItems: 'center',
                px: { xs: 2, sm: 2.5 },
                py: 1.5,
                borderTop: index ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}` : 'none',
              }}
            >
              <Box aria-label={checkStatusLabel(check)} role="img" sx={{ display: 'flex' }}>
                <CheckIcon status={check.status} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary' }}>
                  {check.label}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                  {checkDetail(check)}
                  {action === 'withdraw' && (
                    <>
                      {' '}
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => setA2pDialog('withdraw')}
                        sx={{ fontSize: '12.5px', fontWeight: 600, verticalAlign: 'baseline' }}
                      >
                        Withdraw
                      </Link>
                    </>
                  )}
                </Typography>
                {action === 'record' && (
                  <SharedButton
                    label="Record confirmation…"
                    btnVariant="secondary"
                    size="small"
                    onClick={() => setA2pDialog('record')}
                    sx={{ mt: 1, py: 0.5, px: 1.5, fontSize: '12.5px' }}
                  />
                )}
              </Box>
              <Chip tone={check.status} />
            </Box>
          )
        })}
      </Paper>

      {a2pDialog === 'record' && id && (
        <A2pRecordDialog companyId={id} companyName={companyName} onClose={() => setA2pDialog(null)} />
      )}
      {a2pDialog === 'withdraw' && id && <A2pWithdrawDialog companyId={id} onClose={() => setA2pDialog(null)} />}

      {pending && copy && (
        <SharedDialog variant="form" open onClose={closeDialog} title={copy.title} actions={null} maxWidth={520}>
          <Box component="form" onSubmit={(event: React.FormEvent) => void onConfirm(event)} noValidate>
            <DialogContent sx={{ pt: 1, pb: 1 }}>
              {copy.introCompany && (
                <Typography sx={{ fontSize: '14px', color: 'text.primary', mb: 0.75 }}>
                  For <strong>{copy.introCompany}</strong>, from now on:
                </Typography>
              )}
              <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.5 }}>
                {copy.bullets.map((bullet) => (
                  <Typography
                    component="li"
                    key={bullet.text}
                    sx={{ fontSize: '14px', color: 'text.primary', my: 0.5 }}
                  >
                    {bullet.strong && <strong>{bullet.strong} </strong>}
                    {bullet.text}
                  </Typography>
                ))}
              </Box>
              <InputWithValidation
                name="reason"
                control={control}
                errors={errors}
                rules={{
                  validate: (value: string) => value.trim().length > 0 || 'A reason is required',
                  maxLength: {
                    value: REASON_MAX_LENGTH,
                    message: `Keep the reason under ${REASON_MAX_LENGTH} characters`,
                  },
                }}
                label="Reason (required, saved to the audit log)"
                placeholder="Why is this being changed?"
                autoFocus
                sx={getDialogTextFieldSx(isDark)}
              />
              {copy.requiresAttestation && (
                <FormControlLabel
                  sx={{
                    mt: 1.5,
                    alignItems: 'flex-start',
                    '& .MuiFormControlLabel-label': { fontSize: '14px', pt: 0.75 },
                  }}
                  control={<Checkbox checked={attested} onChange={(event) => setAttested(event.target.checked)} />}
                  label="I confirmed the readiness checks above with the company."
                />
              )}
              {submitError && <DialogError message={submitError} />}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1.5 }}>
              <SharedButton label="Cancel" btnVariant="secondary" fullWidth onClick={closeDialog} disabled={saving} />
              <SharedButton
                label={copy.confirmLabel}
                fullWidth
                type="submit"
                loading={saving}
                disabled={!canConfirm(reason, pending.target, attested)}
              />
            </DialogActions>
          </Box>
        </SharedDialog>
      )}
    </Box>
  )
}

export default ESignature
